/**
 * SNES Synth Instrument Adapter
 *
 * Wraps SnesSynth to implement the Instrument interface for the composition engine.
 * Handles timed command scheduling via the worklet's command queue.
 */

import type { SnesSynth, EnvelopeOptions } from '../../SnesSynth.js';
import type { PlaybackInstrument } from './Transport.js';

// =============================================================================
// Scheduled Command Types
// =============================================================================

/**
 * A scheduled note-on command.
 */
export interface ScheduledNoteOn {
  readonly type: 'scheduledNoteOn';
  readonly voice: number;
  readonly pitch: number;
  readonly velocity: number;
  readonly time: number; // AudioContext time in seconds
}

/**
 * A scheduled note-off command.
 */
export interface ScheduledNoteOff {
  readonly type: 'scheduledNoteOff';
  readonly voice: number;
  readonly time: number; // AudioContext time in seconds
}

/**
 * All scheduled command types.
 */
export type ScheduledCommand = ScheduledNoteOn | ScheduledNoteOff;

// =============================================================================
// Instrument Mapping
// =============================================================================

/**
 * Configuration for how an instrument ID maps to samples/voices.
 */
export interface SynthInstrumentConfig {
  /** Base MIDI note for the sample (default 60 = middle C) */
  baseNote: number;
  /** Default velocity scaling (0-1) */
  velocityScale: number;
}

// =============================================================================
// SnesSynthInstrument
// =============================================================================

/**
 * SNES Synth instrument adapter.
 *
 * Converts the Transport's Instrument interface calls into scheduled
 * commands for the SnesSynth/worklet system.
 *
 * Architecture:
 * - The Transport calls scheduleNoteOn/scheduleNoteOff with AudioContext times
 * - This adapter converts to worklet commands with scheduled times
 * - The worklet processes commands when their time arrives
 */
export class SnesSynthInstrument implements PlaybackInstrument {
  private readonly synth: SnesSynth;
  private readonly instrumentConfigs: Map<string, SynthInstrumentConfig> = new Map();
  private readonly envelopeLibrary: Map<string, EnvelopeOptions> = new Map();

  // Default config for unknown instruments
  private readonly defaultConfig: SynthInstrumentConfig = {
    baseNote: 60,
    velocityScale: 1.0,
  };

  constructor(synth: SnesSynth) {
    this.synth = synth;
  }

  /**
   * Register an instrument with its configuration.
   */
  registerInstrument(instrumentId: string, config: Partial<SynthInstrumentConfig>): void {
    this.instrumentConfigs.set(instrumentId, {
      ...this.defaultConfig,
      ...config,
    });
  }

  /**
   * Get the current AudioContext time.
   */
  getCurrentTime(): number {
    const ctx = this.synth.getAudioContext();
    return ctx?.currentTime ?? 0;
  }

  /**
   * Schedule a note-on at a specific AudioContext time.
   *
   * @param voice Voice index (0-7)
   * @param instrumentId Instrument ID for sample/envelope lookup
   * @param midiNote MIDI note number (0-127)
   * @param _velocity Velocity 0-1 (currently unused)
   * @param time AudioContext time to start the note
   *
   * This uses sample-accurate scheduling via the worklet's command queue.
   * Also schedules voice configuration (sample source + envelope) just before noteOn.
   */
  scheduleNoteOn(
    voice: number,
    instrumentId: string,
    midiNote: number,
    _velocity: number,
    time: number
  ): void {
    // Schedule voice configuration just before noteOn (1ms before)
    const configTime = time - 0.001;

    // Set voice to use this instrument's sample from the library
    this.synth.scheduleSetVoiceSource(voice, instrumentId, configTime);

    // Set envelope for this instrument
    const envelope = this.envelopeLibrary.get(instrumentId);
    if (envelope) {
      this.synth.scheduleSetEnvelope(voice, envelope, configTime);
    }

    // Convert MIDI note to pitch multiplier using the instrument's baseNote
    const config = this.instrumentConfigs.get(instrumentId) ?? this.defaultConfig;
    const pitch = Math.pow(2, (midiNote - config.baseNote) / 12);

    // Send scheduled noteOn command to the worklet for sample-accurate timing
    this.synth.scheduleNoteOn(voice, pitch, time);
  }

  /**
   * Schedule a note-off at a specific AudioContext time.
   *
   * This uses sample-accurate scheduling via the worklet's command queue.
   */
  scheduleNoteOff(voice: number, time: number): void {
    this.synth.scheduleNoteOff(voice, time);
  }

  /**
   * Initialize time synchronization with the worklet.
   * Call this when starting playback to establish the time reference.
   */
  syncTime(): void {
    this.synth.syncTime();
  }

  /**
   * Clear all pending scheduled commands.
   * Call this on stop/seek to prevent stale commands from executing.
   */
  clearScheduledCommands(): void {
    this.synth.clearScheduledCommands();
  }

  /**
   * Load a sample for an instrument.
   *
   * This sends the sample to the worklet's sample library, indexed by instrument ID.
   * The sample will be assigned to voices dynamically when notes are scheduled.
   */
  loadInstrumentSample(
    instrumentId: string,
    sampleData: Float32Array,
    options: {
      loopStart?: number;
      loopEnd?: number;
      loopEnabled?: boolean;
      baseNote?: number;
      sampleRate?: number;
    }
  ): void {
    // Send sample to worklet's library
    this.synth.loadSampleToLibrary(instrumentId, sampleData, {
      sampleRate: options.sampleRate,
      loopStart: options.loopStart,
      loopEnd: options.loopEnd,
      loopEnabled: options.loopEnabled,
    });

    // Register the instrument config for pitch calculations
    if (options.baseNote !== undefined) {
      this.registerInstrument(instrumentId, { baseNote: options.baseNote });
    }
  }

  /**
   * Set envelope configuration for an instrument.
   *
   * This stores the envelope locally and schedules it when notes play.
   */
  setInstrumentEnvelope(instrumentId: string, options: EnvelopeOptions): void {
    this.envelopeLibrary.set(instrumentId, options);
  }

  /**
   * Get the synth instance (for advanced usage).
   */
  getSynth(): SnesSynth {
    return this.synth;
  }
}

// =============================================================================
// Factory Function
// =============================================================================

/**
 * Create a SnesSynthInstrument from a SnesSynth instance.
 */
export function createSnesSynthInstrument(synth: SnesSynth): SnesSynthInstrument {
  return new SnesSynthInstrument(synth);
}
